<template>
  <div class="survey-management pb-5">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
      <div class="d-flex align-items-center text-muted">
        <span class="text-primary fw-medium me-2">Survey Management</span> / <span class="ms-2 text-dark">{{ isEditing ?
          'Edit Survey' : 'Create Survey' }}</span>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-danger px-4 rounded-pill fw-medium" @click="cancel">Cancel</button>
        <button class="btn btn-outline-primary px-4 rounded-pill fw-medium">Update as Draft</button>
        <button class="btn btn-primary-custom px-4 rounded-pill fw-medium" @click="saveSurvey">Update Survey</button>
      </div>
    </div>

    <div class="row g-4">
      <!-- Left Column: Details & Fields -->
      <div class="col-md-5">
        <!-- Survey Details Card -->
        <div class="card tc-app-card mb-4">
          <div class="card-header bg-white border-0 pt-4 pb-2 px-4">
            <h6 class="fw-bold text-dark mb-0 fs-6">Survey Details</h6>
          </div>
          <div class="card-body px-4 pb-4">
            <div class="mb-4">
              <label class="form-label small fw-bold text-dark">Survey Title <span class="text-danger">*</span></label>
              <input type="text" class="form-control tc-app-input" v-model="survey.title"
                placeholder="Hospitality Survey" />
            </div>
            <div class="mb-2">
              <label class="form-label small fw-bold text-dark">Description <span class="text-danger">*</span></label>
              <textarea class="form-control tc-app-input" v-model="survey.description" rows="4"
                placeholder="Description about the survey"></textarea>
            </div>
          </div>
        </div>

        <!-- Add Fields Card -->
        <div class="card tc-app-card">
          <div class="card-header bg-white border-0 pt-4 pb-2 px-4">
            <h6 class="fw-bold text-dark mb-0 fs-6">Add Questions</h6>
          </div>
          <div class="card-body px-4 pb-4">
            <div class="row g-2">
              <div class="col-4" v-for="field in fieldTypes" :key="field.type">
                <button
                  class="btn btn-field w-100 h-100 d-flex flex-column align-items-center justify-content-center py-3"
                  @click="addQuestion(field.type)">
                  <i :class="field.icon" class="mb-2 fs-5"></i>
                  <span class="small fw-medium">{{ field.label }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Questions Canvas -->
      <div class="col-md-7">
        <div class="card tc-app-card h-100" style="background-color: #F8F9FC;">
          <div class="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <i class="fa fa-question-circle text-primary me-2"></i>
              <h6 class="fw-bold text-dark mb-0 fs-6">Questions</h6>
            </div>
            <span class="badge bg-light text-dark border px-3 py-2 rounded-pill fw-medium">- {{ survey.questions.length
              < 10 ? '0' + survey.questions.length : survey.questions.length }} Added</span>
          </div>
          <div class="card-body px-4 pb-4 overflow-auto" style="max-height: 750px;">

            <div v-if="survey.questions.length === 0" class="text-muted text-center py-5 mt-5">
              <p>Click on the fields to the left to add questions to your survey.</p>
            </div>

            <div v-for="(q, index) in survey.questions" :key="index" class="card question-item-card mb-3 border-0">
              <div class="card-body p-3 d-flex align-items-start">
                <div class="flex-grow-1">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <div class="d-flex align-items-center">
                      <i :class="getIconForType(q.question_type)" class="text-muted me-2"></i>
                      <span class="fw-bold text-dark fs-6">{{ q.question_type }}</span>
                    </div>
                    <div class="d-flex align-items-center gap-3">
                      <span class="badge rounded-pill fw-medium px-3 py-1 cursor-pointer select-none"
                        :class="q.is_required ? 'bg-primary-subtle text-primary' : 'bg-light text-muted border'"
                        @click="q.is_required = !q.is_required">
                        {{ q.is_required ? 'Required' : 'Not Required' }}
                      </span>
                      <button class="btn btn-sm btn-link text-primary p-0 text-decoration-none" title="Toggle Required"
                        @click="q.is_required = !q.is_required"><i class="fa fa-pen"></i></button>
                      <button class="btn btn-sm btn-link text-danger p-0 text-decoration-none" title="Delete"
                        @click="removeQuestion(index)"><i class="fa fa-trash"></i></button>
                    </div>
                  </div>
                  <div class="mb-1 text-muted small">
                    <input type="text" class="form-control tc-app-input mt-2 mb-2" v-model="q.question_text"
                      placeholder="Enter your question here" />
                  </div>

                  <!-- Dynamic Options Rendering for Builder -->
                  <div v-if="['Single Select', 'Multi Select', 'Dropdown'].includes(q.question_type)"
                    class="mt-3 ps-3 border-start border-2 border-primary-subtle">
                    <div v-for="(opt, oIndex) in q.options" :key="oIndex" class="d-flex mb-2 align-items-center">
                      <i class="fa fa-circle text-muted me-2" style="font-size: 0.4rem;"></i>
                      <input type="text" class="form-control form-control-sm tc-app-input me-2"
                        v-model="q.options[oIndex]" placeholder="Option text..." />
                      <button class="btn btn-sm text-danger p-0" @click="q.options.splice(oIndex, 1)"><i
                          class="fa fa-times"></i></button>
                    </div>
                    <button class="btn btn-sm text-primary p-0 fw-medium mt-2 d-flex align-items-center"
                      @click="q.options.push('')">
                      <i class="fa fa-plus-circle me-1"></i> Add Option
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SurveyBuilder',
  data() {
    return {
      isEditing: false,
      surveyId: null,
      fieldTypes: [
        { type: 'Short Text', label: 'Short Text', icon: 'fa fa-font' },
        { type: 'Single Select', label: 'Single Select', icon: 'fa fa-dot-circle' },
        { type: 'Multi Select', label: 'Multi Select', icon: 'fa fa-check-square' },
        { type: 'Dropdown', label: 'Dropdown', icon: 'fa fa-list' },
        { type: 'Rating Scale', label: 'Rating Scale', icon: 'fa fa-star' },
        { type: 'Number', label: 'Number', icon: 'fa fa-hashtag' }
      ],
      survey: {
        title: '',
        description: '',
        questions: []
      }
    };
  },
  async mounted() {
    // If editing existing
    if (this.$route.params.id) {
      this.isEditing = true;
      this.surveyId = this.$route.params.id;
      await this.fetchSurvey();
    }
  },
  methods: {
    async fetchSurvey() {
      try {
        const token = localStorage.getItem('jwt');
        const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';
        const res = await axios.get(`${baseUrl}/api/surveys/${this.surveyId}?populate=questions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = res.data.data;
        if (data) {
          this.survey.title = data.attributes.title;
          this.survey.description = data.attributes.description;
          this.survey.questions = data.attributes.questions || [];
        }
      } catch (e) {
        console.error(e);
      }
    },
    getIconForType(type) {
      const field = this.fieldTypes.find(f => f.type === type);
      return field ? field.icon : 'fa fa-question';
    },
    addQuestion(type) {
      this.survey.questions.push({
        __component: 'survey.question',
        question_text: '',
        question_type: type,
        is_required: false,
        options: ['Single Select', 'Multi Select', 'Dropdown'].includes(type) ? ['Option 1'] : null
      });
    },
    removeQuestion(index) {
      this.survey.questions.splice(index, 1);
    },
    cancel() {
      this.$bvModal.msgBoxConfirm("Are you sure you want to cancel? Unsaved changes will be lost.", {
        title: 'Cancel Editing',
        okVariant: 'danger',
        okTitle: 'Yes, Cancel',
        cancelTitle: 'No, Keep Editing',
        centered: true
      }).then(value => {
        if (value) {
          this.$router.push('/survey-management');
        }
      });
    },
    async saveSurvey() {
      if (!this.survey.title) {
        this.$bvToast.toast("Survey Title is required", { title: 'Validation Error', variant: 'warning', solid: true });
        return;
      }
      try {
        const payload = {
          data: {
            title: this.survey.title,
            description: this.survey.description,
            questions: this.survey.questions
          }
        };
        const token = localStorage.getItem('jwt');
        const headers = { Authorization: `Bearer ${token}` };
        const baseUrl = process.env.VUE_APP_BASE_URL || 'http://localhost:1337';

        let response;
        if (this.isEditing) {
          response = await axios.put(`${baseUrl}/api/surveys/${this.surveyId}`, payload, { headers });
        } else {
          response = await axios.post(`${baseUrl}/api/surveys`, payload, { headers });
        }
        this.isEditing = true;
        this.surveyId = response.data.data.id;
        this.$bvToast.toast("Survey saved successfully!", { title: 'Success', variant: 'success', solid: true });
        this.$router.push('/survey-management');
      } catch (err) {
        console.error(err);
        this.$bvToast.toast('Error saving survey.', { title: 'Error', variant: 'danger', solid: true });
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

.survey-management {
  font-family: 'Outfit', sans-serif;
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 2rem;
  background-color: #ffffff;
}

.text-primary {
  color: #6C5DD3 !important;
}

.bg-primary-subtle {
  background-color: #F0EDFF !important;
}

.border-primary-subtle {
  border-color: #F0EDFF !important;
}

.btn-primary-custom {
  background-color: #6C5DD3;
  border-color: #6C5DD3;
  color: white;
}

.btn-primary-custom:hover {
  background-color: #5b4eb8;
  color: white;
}

.btn-outline-primary {
  color: #6C5DD3;
  border-color: #E2E2EA;
}

.btn-outline-primary:hover {
  background-color: #F0EDFF;
  border-color: #6C5DD3;
}

.btn-outline-danger {
  color: #FF754C;
  border-color: #FF754C;
}

.btn-outline-danger:hover {
  background-color: #FFF0ED;
  color: #FF754C;
}

.tc-app-card {
  border: 1px solid #E2E2EA;
  border-radius: 12px;
  box-shadow: none;
}

.tc-app-input {
  border: 1px solid #E2E2EA;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  font-size: 0.95rem;
}

.tc-app-input:focus {
  border-color: #6C5DD3;
  box-shadow: 0 0 0 0.2rem rgba(108, 93, 211, 0.15);
}

.btn-field {
  background-color: #ffffff;
  border: 1px solid #E2E2EA;
  border-radius: 8px;
  color: #11142D;
  transition: all 0.2s;
}

.btn-field i {
  color: #6C5DD3;
}

.btn-field:hover {
  border-color: #6C5DD3;
  background-color: #F8F9FC;
  transform: translateY(-2px);
}

.question-item-card {
  border-radius: 12px;
  border: 1px solid #E2E2EA !important;
  background-color: #ffffff;
}

.drag-handle {
  cursor: grab;
  color: #B2B2BF;
}

.cursor-pointer {
  cursor: pointer;
}

.select-none {
  user-select: none;
}
</style>
